<template>
    <main v-if="company" id="js-page-content" class="cp-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/admin/companies" class="tbv2-crumb">{{ $t('admin.companies.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ company.name }}</span>
            </div>
        </portal>

        <header class="cp-hero">
            <router-link to="/admin/companies" class="cp-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <div class="cp-hero-row">
                <div class="cp-hero-icon">
                    <i class="fal fa-building"></i>
                </div>
                <div class="cp-hero-body">
                    <h1 class="cp-hero-title">{{ company.name }}</h1>
                    <div class="cp-hero-pills">
                        <span class="cp-pill cp-pill--id">#{{ company.id }}</span>
                        <span v-if="company.status" class="cp-pill" :class="statusToneClass(company.status)">
                            {{ company.status.name }}
                        </span>
                    </div>
                </div>
                <div class="cp-hero-actions">
                    <button
                        type="button"
                        class="cp-toggle-btn"
                        :class="company.active ? 'cp-toggle-btn--danger' : 'cp-toggle-btn--success'"
                        :disabled="toggling"
                        @click="confirmToggle"
                    >
                        <i v-if="toggling" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else :class="company.active ? 'fal fa-power-off' : 'fal fa-bolt'"></i>
                        <span>{{ company.active ? $t('common.deactivate') || 'Désactiver' : $t('common.activate') || 'Activer' }}</span>
                    </button>
                    <router-link :to="`/admin/companies/${company.id}/edit`" class="cp-edit-btn">
                        <i class="fal fa-pen"></i>
                        <span>{{ $t('common.edit') }}</span>
                    </router-link>
                </div>
            </div>
        </header>

        <section v-if="companyStats" class="cp-stats">
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--cyan"><i class="fal fa-users"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.users_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.users') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--teal"><i class="fal fa-user-friends"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.citizens_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.citizens') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--purple"><i class="fal fa-file-alt"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.forms_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.forms') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--pink"><i class="fal fa-layer-group"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.form_types_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.form_types') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--indigo"><i class="fal fa-ticket"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.posts_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.posts') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--amber"><i class="fal fa-inbox-in"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.open_posts_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.open_posts') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--green"><i class="fal fa-check-circle"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.closed_posts_count) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.closed_posts') }}</div>
                </div>
            </div>
            <div class="cp-stat">
                <div class="cp-stat-icon cp-stat-icon--orange"><i class="fal fa-calendar-week"></i></div>
                <div class="cp-stat-data">
                    <div class="cp-stat-value">{{ formatNumber(companyStats.posts_last_7_days) }}</div>
                    <div class="cp-stat-label">{{ $t('admin.companies.stats.last_7_days') }}</div>
                </div>
            </div>
        </section>

        <div class="cp-grid">
            <div class="cp-col cp-col--main">
                <section v-if="postsByStatus.length" class="cp-card">
                    <header class="cp-card-header">
                        <div class="cp-card-icon">
                            <i class="fal fa-chart-bar"></i>
                        </div>
                        <h2 class="cp-card-title">{{ $t('admin.companies.stats.by_status') }}</h2>
                    </header>
                    <div class="cp-status-list">
                        <div
                            v-for="entry in postsByStatus"
                            :key="entry.status_id"
                            class="cp-status-row"
                        >
                            <div class="cp-status-meta">
                                <span class="cp-status-name">{{ entry.status_name }}</span>
                                <span class="cp-status-count">{{ formatNumber(entry.total) }}</span>
                            </div>
                            <div class="cp-status-bar">
                                <div
                                    class="cp-status-bar-fill"
                                    :style="{ width: statusBarWidth(entry) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="cp-card">
                    <header class="cp-card-header">
                        <div class="cp-card-icon">
                            <i class="fal fa-clock"></i>
                        </div>
                        <h2 class="cp-card-title">{{ $t('admin.companies.stats.recent') }}</h2>
                    </header>
                    <div v-if="!recentPosts.length" class="cp-empty">
                        <i class="fal fa-inbox"></i>
                        <p>{{ $t('admin.companies.stats.no_posts') }}</p>
                    </div>
                    <div v-else class="cp-table-wrapper">
                        <table class="cp-table">
                            <thead>
                                <tr>
                                    <th class="cp-table-th-narrow">Réf.</th>
                                    <th>Titre</th>
                                    <th class="cp-table-th-narrow">Priorité</th>
                                    <th class="cp-table-th-narrow">Créé</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="post in recentPosts"
                                    :key="post.id"
                                    class="cp-post-row"
                                    @click="goToPost(post.id)"
                                >
                                    <td class="cp-cell-ref">
                                        <router-link :to="`/posts/${post.id}`" class="cp-post-link">
                                            {{ post.reference }}
                                        </router-link>
                                    </td>
                                    <td class="cp-cell-title">{{ post.title || '—' }}</td>
                                    <td>
                                        <span class="cp-priority-pill" :class="priorityClass(post.priority)">
                                            P{{ post.priority || 0 }}
                                        </span>
                                    </td>
                                    <td class="cp-cell-date">{{ formatDate(post.created_at) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <div class="cp-col cp-col--side">

        <section class="cp-card">
            <header class="cp-card-header">
                <div class="cp-card-icon">
                    <i class="fal fa-info-circle"></i>
                </div>
                <h2 class="cp-card-title">{{ $t('post.page.information.general') }}</h2>
            </header>
            <dl class="cp-info-grid">
                <div class="cp-info">
                    <dt class="cp-info-label">{{ $t('post.page.information.reference') }}</dt>
                    <dd class="cp-info-value cp-info-value--mono">#{{ company.id }}</dd>
                </div>
                <div class="cp-info">
                    <dt class="cp-info-label">{{ $t('companies.name') }}</dt>
                    <dd class="cp-info-value">{{ company.name }}</dd>
                </div>
                <div v-if="company.slug" class="cp-info">
                    <dt class="cp-info-label">{{ $t('companies.slug') }}</dt>
                    <dd class="cp-info-value cp-info-value--mono">{{ company.slug }}</dd>
                </div>
                <div v-if="company.status" class="cp-info">
                    <dt class="cp-info-label">{{ $t('admin.state') }}</dt>
                    <dd class="cp-info-value">{{ company.status.name }}</dd>
                </div>
            </dl>
        </section>

        <section v-if="companySettings" class="cp-card">
            <header class="cp-card-header">
                <div class="cp-card-icon">
                    <i class="fal fa-cog"></i>
                </div>
                <h2 class="cp-card-title">{{ $t('companies.settings.name') || 'Paramètres' }}</h2>
            </header>
            <div class="cp-settings-body">
                <div class="cp-logo-preview">
                    <div class="cp-logo-frame" :class="{ 'cp-logo-frame--empty': !logoSrc }">
                        <img v-if="logoSrc" :src="logoSrc" alt="Logo" class="cp-logo-img" />
                        <i v-else class="fal fa-image cp-logo-placeholder"></i>
                    </div>
                    <div class="cp-logo-meta">
                        <span class="cp-info-label">Logo</span>
                        <span class="cp-logo-mode">{{ logoModeLabel }}</span>
                    </div>
                </div>
                <dl class="cp-info-grid cp-info-grid--compact">
                    <div v-if="companySettings.mail_admin" class="cp-info">
                        <dt class="cp-info-label">{{ $t('companies.settings.contact.mail_admin') || 'Mail admin' }}</dt>
                        <dd class="cp-info-value">
                            <a :href="`mailto:${companySettings.mail_admin}`" class="cp-mail-link">
                                {{ companySettings.mail_admin }}
                            </a>
                        </dd>
                    </div>
                </dl>
            </div>
        </section>

        <section v-if="headerMailHtml" class="cp-card">
            <header class="cp-card-header">
                <div class="cp-card-icon">
                    <i class="fal fa-envelope"></i>
                </div>
                <h2 class="cp-card-title">{{ $t('companies.settings.mail.template') || 'Modèle d\'en-tête email' }}</h2>
            </header>
            <div class="cp-mail-preview" v-html="headerMailHtml"></div>
        </section>
            </div>
        </div>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import LOGO_SETTINGS from '@/helpers/logoSettings.js'

const DEFAULT_LOGO = '/img/logo-1.png'

export default {
    name: 'Company',
    data() {
        return {
            loading: true,
            toggling: false,
            LOGO_SETTINGS
        }
    },
    created() {
        const id = this.$route.params.id
        this.getCompany(id)
            .catch((e) => {
                this.$store.dispatch('dispatchError', e)
            })
            .finally(() => {
                this.loading = false
            })
    },
    computed: {
        ...mapState('admin', {
            company: (state) => state.company,
            companyStats: (state) => state.companyStats,
            companySettings: (state) => state.companySettings,
            companyHeaderMail: (state) => state.companyHeaderMail
        }),
        ...mapState('auth', { token: (state) => state.token }),
        logoMode() {
            return this.companySettings && this.companySettings.logo_settings
                ? this.companySettings.logo_settings
                : LOGO_SETTINGS.DEFAULT_LOGO
        },
        logoSrc() {
            if (this.logoMode === LOGO_SETTINGS.NO_LOGO) return null
            if (this.logoMode === LOGO_SETTINGS.CUSTOM_LOGO && this.companySettings) {
                const current = (this.companySettings.settings_logo || []).find((l) => l.is_logo)
                if (current && this.company) {
                    return `${process.env.VUE_APP_BACKEND_URL}/api/${this.company.id}/storage/logo/${current.id}?token=${this.token}`
                }
            }
            return DEFAULT_LOGO
        },
        logoModeLabel() {
            if (this.logoMode === LOGO_SETTINGS.NO_LOGO) return this.$t('companies.settings.logo_config.no_logo') || 'Aucun logo'
            if (this.logoMode === LOGO_SETTINGS.CUSTOM_LOGO) return 'Logo personnalisé'
            return 'Logo par défaut'
        },
        headerMailHtml() {
            return this.companyHeaderMail && this.companyHeaderMail.settings_mail_template
                ? this.companyHeaderMail.settings_mail_template
                : ''
        },
        postsByStatus() {
            return (this.companyStats && Array.isArray(this.companyStats.posts_by_status))
                ? this.companyStats.posts_by_status
                : []
        },
        recentPosts() {
            return (this.companyStats && Array.isArray(this.companyStats.recent_posts))
                ? this.companyStats.recent_posts
                : []
        },
        maxStatusTotal() {
            if (!this.postsByStatus.length) return 0
            return Math.max(...this.postsByStatus.map((s) => Number(s.total) || 0))
        }
    },
    methods: {
        ...mapActions('admin', ['getCompany', 'updateCompanyActive']),
        confirmToggle() {
            const willDeactivate = !!this.company.active
            const title = willDeactivate
                ? this.$t('admin.companies.deactivate_confirm')
                : this.$t('admin.companies.activate_confirm')
            const confirmLabel = willDeactivate
                ? this.$t('common.deactivate')
                : this.$t('common.activate')

            const dialog = this.$bmvDialog
            const confirmed = dialog && typeof dialog.confirm === 'function'
                ? dialog.confirm({
                      variant: willDeactivate ? 'danger' : 'primary',
                      title,
                      confirmLabel,
                      cancelLabel: this.$t('common.cancel')
                  })
                : Promise.resolve(window.confirm(title))

            confirmed
                .then((ok) => {
                    if (!ok) return
                    this.toggleActive(!willDeactivate)
                })
                .catch(() => { /* dismiss */ })
        },
        toggleActive(active) {
            this.toggling = true
            this.updateCompanyActive({ id: this.company.id, active })
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.toggling = false
                })
        },
        formatNumber(value) {
            const n = Number(value) || 0
            return new Intl.NumberFormat(this.$i18n.locale).format(n)
        },
        statusToneClass(status) {
            const name = (status && status.name ? status.name : '').toLowerCase()
            if (name.includes('actif') || name.includes('active') || name.includes('valid')) return 'cp-pill--yes'
            if (name.includes('suspend') || name.includes('block') || name.includes('inactif')) return 'cp-pill--no'
            return 'cp-pill--neutral'
        },
        statusBarWidth(entry) {
            const total = Number(entry.total) || 0
            if (!this.maxStatusTotal) return 0
            return Math.max(4, Math.round((total / this.maxStatusTotal) * 100))
        },
        priorityClass(priority) {
            const p = Number(priority) || 0
            if (p >= 4) return 'cp-priority-pill--danger'
            if (p === 3) return 'cp-priority-pill--warning'
            if (p === 2) return 'cp-priority-pill--info'
            return 'cp-priority-pill--neutral'
        },
        formatDate(raw) {
            if (!raw) return ''
            const date = new Date(raw)
            if (Number.isNaN(date.getTime())) return raw
            return new Intl.DateTimeFormat(this.$i18n.locale, {
                day: '2-digit', month: 'short', year: 'numeric'
            }).format(date)
        },
        goToPost(id) {
            if (id) this.$router.push(`/posts/${id}`)
        }
    }
}
</script>

<style scoped>
.cp-v2 {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.cp-hero {
    margin-bottom: var(--v2-space-5);
}

.cp-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 12px;
    transition: color var(--v2-transition-fast);
}

.cp-back:hover {
    color: var(--v2-primary);
}

.cp-back i {
    font-size: 0.75rem;
}

.cp-hero-row {
    display: flex;
    align-items: center;
    gap: 16px;
}

.cp-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    flex-shrink: 0;
}

.cp-hero-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    flex: 1;
}

.cp-hero-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.cp-hero-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.cp-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
}

.cp-pill--id {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-variant-numeric: tabular-nums;
}

.cp-pill--yes { background: rgba(16, 185, 129, 0.12); color: #059669; }
.cp-pill--no { background: rgba(239, 68, 68, 0.12); color: #dc2626; }
.cp-pill--neutral { background: var(--v2-surface-2); color: var(--v2-text-3); }

.cp-hero-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.cp-edit-btn,
.cp-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    border: 1px solid transparent;
    cursor: pointer;
    font-family: inherit;
    flex-shrink: 0;
}

.cp-edit-btn {
    background: var(--v2-primary);
    color: #fff !important;
}

.cp-edit-btn i {
    color: #fff !important;
    font-size: 0.8rem;
}

.cp-edit-btn:hover {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.cp-toggle-btn i {
    font-size: 0.8rem;
}

.cp-toggle-btn--danger {
    background: transparent;
    color: var(--v2-danger);
    border-color: rgba(239, 68, 68, 0.35);
}

.cp-toggle-btn--danger:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.08);
    border-color: var(--v2-danger);
}

.cp-toggle-btn--success {
    background: transparent;
    color: #059669;
    border-color: rgba(16, 185, 129, 0.35);
}

.cp-toggle-btn--success:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.08);
    border-color: #10b981;
}

.cp-toggle-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.cp-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-4);
}

.cp-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: var(--v2-space-4);
    align-items: flex-start;
}

.cp-col {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
    min-width: 0;
}

.cp-stat {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 16px 18px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.cp-stat-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
}

.cp-stat-icon--cyan { background: rgba(8, 145, 178, 0.1); color: #0891B2; }
.cp-stat-icon--teal { background: rgba(20, 184, 166, 0.12); color: #0d9488; }
.cp-stat-icon--purple { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
.cp-stat-icon--pink { background: rgba(236, 72, 153, 0.1); color: #db2777; }
.cp-stat-icon--indigo { background: rgba(99, 102, 241, 0.1); color: #6366f1; }
.cp-stat-icon--amber { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }
.cp-stat-icon--green { background: rgba(16, 185, 129, 0.1); color: #10b981; }
.cp-stat-icon--orange { background: rgba(249, 115, 22, 0.12); color: #ea580c; }

.cp-stat-data {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.cp-stat-value {
    font-size: 1.35rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
}

.cp-stat-label {
    font-size: 0.68rem;
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1.2;
}

.cp-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.cp-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.cp-card-icon {
    color: var(--v2-primary);
    font-size: 1rem;
}

.cp-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.cp-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 32px;
    padding: 22px;
    margin: 0;
}

.cp-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.cp-info-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.cp-info-value {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    word-break: break-word;
}

.cp-info-value--mono {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.82rem;
    color: var(--v2-text-2);
}

.cp-mail-link {
    color: var(--v2-primary);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.cp-mail-link:hover {
    color: var(--v2-primary-hover, #0e7490);
    text-decoration: underline !important;
}

.cp-settings-body {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.cp-logo-preview {
    display: flex;
    align-items: center;
    gap: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid var(--v2-border-subtle);
}

.cp-logo-frame {
    width: 96px;
    height: 64px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    padding: 6px;
}

.cp-logo-frame--empty {
    color: var(--v2-text-4);
}

.cp-logo-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
}

.cp-logo-placeholder {
    font-size: 1.5rem;
}

.cp-logo-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.cp-logo-mode {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.cp-info-grid--compact {
    padding: 0;
    gap: 16px 32px;
}

.cp-mail-preview {
    padding: 22px;
    background: var(--v2-surface-1);
    border-top: 1px solid var(--v2-border);
    max-height: 320px;
    overflow: auto;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    line-height: 1.5;
}

.cp-mail-preview :deep(img) {
    max-width: 100%;
    height: auto;
}

.cp-mail-preview :deep(a) {
    color: var(--v2-primary);
}

/* Status breakdown */
.cp-status-list {
    padding: 18px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cp-status-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cp-status-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.cp-status-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.cp-status-count {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    font-variant-numeric: tabular-nums;
    font-weight: var(--v2-font-weight-semibold);
}

.cp-status-bar {
    height: 8px;
    background: var(--v2-surface-2);
    border-radius: 999px;
    overflow: hidden;
}

.cp-status-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--v2-primary), #06b6d4);
    border-radius: 999px;
    transition: width 300ms cubic-bezier(0.22, 1, 0.36, 1);
}

/* Recent posts table */
.cp-empty {
    padding: 32px 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--v2-text-4);
}

.cp-empty i {
    font-size: 1.6rem;
}

.cp-empty p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

.cp-table-wrapper {
    overflow-x: auto;
}

.cp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--v2-text-sm-size);
}

.cp-table th {
    text-align: left;
    padding: 10px 16px;
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-size: 0.68rem;
    border-bottom: 1px solid var(--v2-border);
    white-space: nowrap;
}

.cp-table td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--v2-border-subtle);
    color: var(--v2-text-2);
    vertical-align: middle;
}

.cp-table tr:last-child td {
    border-bottom: none;
}

.cp-table-th-narrow { width: 110px; }

.cp-cell-ref {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.78rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-primary);
}

.cp-post-row {
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.cp-post-row:hover {
    background: var(--v2-primary-soft);
}

.cp-post-link {
    color: inherit;
    text-decoration: none !important;
}

.cp-cell-title {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-medium);
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cp-cell-date {
    color: var(--v2-text-3);
    font-size: var(--v2-text-xs-size);
    white-space: nowrap;
}

.cp-priority-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    font-variant-numeric: tabular-nums;
}

.cp-priority-pill--danger { background: rgba(239, 68, 68, 0.12); color: #dc2626; }
.cp-priority-pill--warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.cp-priority-pill--info { background: rgba(8, 145, 178, 0.12); color: #0891B2; }
.cp-priority-pill--neutral { background: var(--v2-surface-2); color: var(--v2-text-3); }

@media (max-width: 1100px) {
    .cp-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 900px) {
    .cp-v2 {
        padding: 16px 18px 32px;
    }

    .cp-hero-row {
        flex-wrap: wrap;
        gap: 12px;
    }

    .cp-hero-title {
        font-size: 1.25rem;
    }

    .cp-edit-btn {
        width: 100%;
        justify-content: center;
    }

    .cp-info-grid {
        grid-template-columns: 1fr;
        gap: 18px;
        padding: 18px;
    }

    .cp-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 500px) {
    .cp-stats {
        grid-template-columns: 1fr;
    }
}
</style>
